/**
 * @nanokart/tryon-studio-sdk
 * useUI hook - Manage UI state
 */

import { useCallback } from 'react';
import { useStore } from './useStore';
import type { ActivePanel } from '../state/types';

export interface UseUIReturn {
  /** Active panel */
  activePanel: ActivePanel;
  /** Open modals */
  modals: Record<string, boolean>;
  /** Current toast */
  toast: { message: string; type: 'success' | 'error' | 'info' } | null;
  /** Is mobile view */
  isMobile: boolean;
  /** Set active panel */
  setActivePanel: (panel: ActivePanel) => void;
  /** Open a modal */
  openModal: (modalId: string) => void;
  /** Close a modal */
  closeModal: (modalId: string) => void;
  /** Check if modal is open */
  isModalOpen: (modalId: string) => boolean;
  /** Show toast message */
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
  /** Clear toast */
  clearToast: () => void;
}

/**
 * Hook for managing UI state
 * 
 * @example
 * ```tsx
 * const { activePanel, setActivePanel, showToast, isModalOpen } = useUI();
 * 
 * return (
 *   <div>
 *     <Tabs value={activePanel} onChange={setActivePanel} />
 *     <Modal open={isModalOpen('filters')} />
 *   </div>
 * );
 * ```
 */
export function useUI(): UseUIReturn {
  const activePanel = useStore((s) => s.ui.activePanel);
  const modals = useStore((s) => s.ui.modals);
  const toast = useStore((s) => s.ui.toast);
  const isMobile = useStore((s) => s.ui.isMobile);
  
  const setActivePanelAction = useStore((s) => s.setActivePanel);
  const openModalAction = useStore((s) => s.openModal);
  const closeModalAction = useStore((s) => s.closeModal);
  const showToastAction = useStore((s) => s.showToast);
  const clearToastAction = useStore((s) => s.clearToast);

  const setActivePanel = useCallback(
    (panel: ActivePanel) => {
      setActivePanelAction(panel);
    },
    [setActivePanelAction]
  );

  const openModal = useCallback(
    (modalId: string) => {
      openModalAction(modalId);
    },
    [openModalAction]
  );

  const closeModal = useCallback(
    (modalId: string) => {
      closeModalAction(modalId);
    },
    [closeModalAction]
  );

  const isModalOpen = useCallback(
    (modalId: string) => {
      return Boolean(modals[modalId]);
    },
    [modals]
  );

  const showToast = useCallback(
    (message: string, type: 'success' | 'error' | 'info' = 'info') => {
      showToastAction(message, type);
    },
    [showToastAction]
  );

  const clearToast = useCallback(() => {
    clearToastAction();
  }, [clearToastAction]);

  return {
    activePanel,
    modals,
    toast,
    isMobile,
    setActivePanel,
    openModal,
    closeModal,
    isModalOpen,
    showToast,
    clearToast,
  };
}
